import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatPriceSimple } from '@/utils/courseFormat'
import KmPromoIcon from '@/components/course/KmPromoIcon'
import { useAuthStore } from '@/store/authStore'
import { shareKmCombo } from '@/utils/shareKmCombo'

export type GiftComboInfo = {
  sourceCourseId: number
  sourceCourseName: string
  sourceCourseCode?: string
  sourceCourseHashId?: string | null
  sourceCourseFee: number
  giftCourseId: number
  giftCourseName: string
  giftCourseCode?: string
  giftCourseHashId?: string | null
  giftCourseFee: number
  originalFee?: number
  comboFee?: number
}

type Props = {
  combo: GiftComboInfo
  /** compact = lộ trình; banner = combo box trang chủ; card = thẻ khóa */
  variant?: 'compact' | 'banner' | 'card'
  className?: string
  /** Học viên đã đăng ký khóa chính — CTA giới thiệu (share) */
  isSourceEnrolled?: boolean
}

const coursePath = (hashId: string | null | undefined, id: number) =>
  `/courses/${hashId || id}`

/**
 * Combo A tặng B — token eh-*; giá rõ: khóa A = fee A, tặng B = 0đ, tổng từ A+B xuống A.
 */
const CourseComboPrice = ({
  combo,
  variant = 'compact',
  className = '',
  isSourceEnrolled = false,
}: Props) => {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [sharing, setSharing] = useState(false)
  const feeA = combo.comboFee ?? combo.sourceCourseFee
  const feeB = combo.giftCourseFee
  const total = combo.originalFee ?? feeA + feeB
  const href = coursePath(combo.sourceCourseHashId, combo.sourceCourseId)

  const goToSourceCourse = () => {
    navigate(href)
  }

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (sharing) return
    setSharing(true)
    try {
      await shareKmCombo({
        sourceCourseName: combo.sourceCourseName,
        giftCourseName: combo.giftCourseName,
        giftCourseFee: feeB,
        sourceCourseHashId: combo.sourceCourseHashId,
        sourceCourseId: combo.sourceCourseId,
        refUserId: user?.id ?? null,
      })
    } catch {
      // toast already shown in helper
    } finally {
      setSharing(false)
    }
  }

  if (variant === 'banner') {
    return (
      <div className={`pt-1 ${className}`}>
        <div
          role="link"
          tabIndex={0}
          aria-label={`Xem khóa ${combo.sourceCourseName}`}
          onClick={goToSourceCourse}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              goToSourceCourse()
            }
          }}
          className="eh-combo-box relative cursor-pointer overflow-hidden rounded-eh border border-eh-border bg-gradient-to-br from-eh-cream to-eh-mint px-5 py-8 sm:px-8 sm:py-10 lg:flex lg:items-stretch lg:gap-10 lg:justify-between lg:px-10 lg:py-12 xl:gap-12 xl:px-12 shadow-eh-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-eh-soft"
        >
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center rounded-eh-pill bg-m2-error px-3.5 py-2 text-xs font-bold tracking-wide text-m2-on-error shadow-eh-soft sm:px-4 sm:text-sm">
                COMBO HOT • Tiết kiệm {formatPriceSimple(feeB)}
              </span>
              <span className="inline-flex items-center rounded-eh-pill bg-eh-mint px-3.5 py-2 text-xs font-bold text-eh-forest-ink sm:px-4 sm:text-sm">
                ƯU ĐÃI KHI ĐĂNG KÝ HÔM NAY
              </span>
            </div>

            <h3 className="mt-6 break-words font-display text-[clamp(1.75rem,3.5vw,2.75rem)] font-semibold leading-snug tracking-tight text-eh-forest-ink">
              Học khóa chính, nhận ngay khóa tặng kèm
            </h3>

            <p className="mt-5 max-w-2xl break-words text-base leading-relaxed text-eh-muted md:text-lg md:leading-8">
              Khi đăng ký{' '}
              <strong className="text-eh-charcoal">{combo.sourceCourseName}</strong>,
              bạn sẽ được tặng miễn phí khóa{' '}
              <strong className="text-eh-charcoal">{combo.giftCourseName}</strong>{' '}
              (trị giá <strong className="text-eh-charcoal">{formatPriceSimple(feeB)}</strong>).
            </p>

            <div className="mt-8 flex min-w-0 flex-col items-stretch gap-3 sm:gap-4 lg:flex-row lg:items-center lg:gap-4">
              <div className="flex min-w-0 flex-1 items-start gap-3 overflow-hidden rounded-eh border border-eh-border bg-white p-4 sm:items-center sm:gap-4 sm:p-5">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-eh-mint text-2xl sm:h-14 sm:w-14 sm:text-3xl"
                  aria-hidden
                >
                  📘
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="break-words text-base font-semibold leading-snug text-eh-forest-ink sm:text-lg">
                    {combo.sourceCourseName}
                  </p>
                  <p className="mt-1 text-sm text-eh-muted">Khóa học chính</p>
                </div>
              </div>

              <span
                className="shrink-0 self-center text-3xl font-bold text-eh-muted sm:text-4xl"
                aria-hidden
              >
                +
              </span>

              <div className="flex min-w-0 flex-1 items-start gap-3 overflow-hidden rounded-eh border-2 border-amber-300 bg-amber-50 p-4 sm:items-center sm:gap-4 sm:p-5">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-eh-mint text-2xl sm:h-14 sm:w-14 sm:text-3xl"
                  aria-hidden
                >
                  🎁
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="break-words text-base font-semibold leading-snug text-eh-forest-ink sm:text-lg">
                    {combo.giftCourseName}
                  </p>
                  <p className="mt-1 text-sm text-eh-muted">Tặng kèm miễn phí</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex w-full min-w-0 shrink-0 flex-col justify-center overflow-hidden rounded-eh bg-white px-6 py-8 text-center shadow-eh-soft sm:px-8 sm:py-10 lg:mt-0 lg:w-[320px] lg:min-h-full xl:w-[340px]">
            <p className="text-base font-bold text-m2-error sm:text-lg">
              Tiết kiệm
              <span className="mt-2 block whitespace-nowrap text-2xl sm:text-3xl">
                {formatPriceSimple(feeB)}
              </span>
            </p>
            <p className="mt-6 whitespace-nowrap text-xl text-eh-muted line-through sm:text-2xl">
              {formatPriceSimple(total)}
            </p>
            <p className="mt-3 whitespace-nowrap font-display text-[clamp(1.75rem,3.8vw,2.75rem)] font-bold leading-none tracking-tight text-eh-forest-ink">
              {formatPriceSimple(feeA)}
            </p>
            {isSourceEnrolled ? (
              <button
                type="button"
                onClick={handleShare}
                disabled={sharing}
                className="eh-btn relative z-10 mt-8 inline-flex w-full !min-h-[52px] justify-center !bg-eh-forest-ink !text-eh-cream hover:!bg-eh-forest-shadow disabled:opacity-60"
              >
                {sharing ? 'Đang chia sẻ…' : (
                  <>
                    Giới thiệu bạn học <span className="eh-arrow">→</span>
                  </>
                )}
              </button>
            ) : (
              <span className="eh-btn mt-8 inline-flex w-full !min-h-[52px] justify-center !bg-eh-forest-ink !text-eh-cream">
                Đăng ký Combo <span className="eh-arrow">→</span>
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (variant === 'card') {
    return (
      <div className={`space-y-1 ${className}`}>
        <div className="flex items-center gap-2">
          <KmPromoIcon size="sm" />
          <span className="text-[11px] font-medium text-eh-muted line-clamp-1">
            + tặng {combo.giftCourseName} (0đ)
          </span>
        </div>
        <p className="text-sm font-medium text-m2-on-surface">
          Học phí khóa này:{' '}
          <span className="text-lg font-bold">{formatPriceSimple(feeA)}</span>
        </p>
        <p className="text-[11px] font-medium text-eh-muted">
          Tổng 2 khóa: từ{' '}
          <span className="line-through">{formatPriceSimple(total)}</span>
          {' '}xuống {formatPriceSimple(feeA)}
        </p>
      </div>
    )
  }

  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`}>
      <KmPromoIcon size="sm" />
      <span className="text-sm font-bold text-eh-forest-ink">{formatPriceSimple(feeA)}</span>
      <span className="text-[11px] font-medium text-eh-muted">
        + tặng 0đ · Tổng từ{' '}
        <span className="line-through">{formatPriceSimple(total)}</span>
        {' '}↓ {formatPriceSimple(feeA)}
      </span>
    </div>
  )
}

export default CourseComboPrice
